// src/components/Hero/Hero.js
import React, { useMemo, useState } from 'react';
import './Hero.css';

const formatPrice = (value) => (
    new Intl.NumberFormat('ru-RU').format(Number(value || 0)) + ' ₽'
);

const formatColorCount = (count) => {
    if (count === 1) return 'Один цвет';
    if (count === 2) return 'Два цвета';
    if (count === 3) return 'Три цвета';
    if (count === 4) return 'Четыре цвета';
    return `${count} цветов`;
};

function Hero({ products = [], loading = false, error = null, onRetry, onProductClick }) {
    const [selectedProductId, setSelectedProductId] = useState(null);

    const collectionProducts = useMemo(() => (
        products.filter((product) => (
            product.category === 'tshirt'
            && product.name?.toLowerCase().includes('наличники')
        ))
    ), [products]);

    const featuredProducts = collectionProducts.length > 0
        ? collectionProducts
        : products.filter((product) => product?.is_active !== false).slice(0, 1);
    const isNalichnikiCollection = collectionProducts.length > 0;
    const blockingError = Boolean(error && featuredProducts.length === 0);

    const selectedProduct = featuredProducts.find((product) => product.id === selectedProductId)
        || featuredProducts.find((product) => (
            product.color_name === 'Розовый'
            && Number(product.total_quantity || 0) > 0
        ))
        || featuredProducts.find((product) => Number(product.total_quantity || 0) > 0)
        || featuredProducts[0]
        || null;

    const mainImage = selectedProduct?.images?.[0] || '';
    const available = Number(selectedProduct?.total_quantity || 0) > 0;
    const imageNote = [selectedProduct?.name, selectedProduct?.color_name]
        .filter(Boolean)
        .join(' · ');

    const handlePrimaryAction = () => {
        if (blockingError) {
            onRetry?.();
            return;
        }
        if (selectedProduct && available) onProductClick?.(selectedProduct);
    };

    return (
        <section className="hero" aria-labelledby="hero-title">
            <div className="hero-visual" aria-live="polite">
                {mainImage ? (
                    <img
                        className="hero-product-image"
                        src={mainImage}
                        alt={`${selectedProduct.name}, цвет ${selectedProduct.color_name}`}
                        loading="eager"
                        fetchPriority="high"
                        decoding="async"
                    />
                ) : (
                    <div className="hero-image-placeholder">
                        {loading
                            ? 'Загружаем коллекцию'
                            : (blockingError ? 'Не удалось загрузить коллекцию' : 'Фотография скоро появится')}
                    </div>
                )}
                <span className="hero-frame-corner hero-frame-corner-top" aria-hidden="true" />
                <span className="hero-frame-corner hero-frame-corner-bottom" aria-hidden="true" />
                <div className="hero-image-note">
                    {imageNote || 'Это Россия'}
                </div>
            </div>

            <div className="hero-content">
                <span className="hero-ornament" aria-hidden="true" />
                <p className="hero-eyebrow">
                    {isNalichnikiCollection ? 'Это Россия · коллекция «Наличники»' : 'Это Россия · одежда с культурным кодом'}
                </p>
                <h1 id="hero-title">Россия —<br />в деталях.</h1>
                <p className="hero-description">
                    {isNalichnikiCollection
                        ? 'Футболки, вдохновлённые резными наличниками. Современная форма для культуры, которую хочется сохранить.'
                        : 'Современная одежда, в которой знакомые символы России становятся частью повседневной жизни.'}
                </p>

                <div className="hero-choice">
                    {blockingError ? (
                        <p className="hero-catalog-error" role="alert">
                            Каталог временно не загрузился. Попробуйте ещё раз.
                        </p>
                    ) : (
                        <>
                            <div>
                                <div className="hero-choice-heading">
                                    <span className="hero-choice-label">Выберите цвет</span>
                                    {selectedProduct?.color_name && (
                                        <span className="hero-selected-color" aria-live="polite">
                                            {selectedProduct.color_name}
                                        </span>
                                    )}
                                </div>
                                <div className="hero-colors" aria-label="Цвет футболки">
                                    {featuredProducts.map((product) => (
                                        <button
                                            key={product.id}
                                            type="button"
                                            className="hero-color"
                                            style={{ '--swatch-color': product.color_code || '#777' }}
                                            onClick={() => setSelectedProductId(product.id)}
                                            aria-label={product.color_name}
                                            aria-pressed={product.id === selectedProduct?.id}
                                            title={product.color_name}
                                        />
                                    ))}
                                </div>
                            </div>
                            <strong className="hero-price">
                                {selectedProduct ? formatPrice(selectedProduct.price) : (loading ? 'Загрузка…' : 'Нет данных')}
                            </strong>
                        </>
                    )}
                </div>

                <div className="hero-actions">
                    <button
                        type="button"
                        className="hero-primary-action"
                        onClick={handlePrimaryAction}
                        disabled={!blockingError && (!selectedProduct || !available)}
                    >
                        {blockingError ? 'Повторить загрузку' : (available ? 'Выбрать цвет и размер' : 'Нет в наличии')}
                    </button>
                    <a className="hero-secondary-action" href="#about">
                        История принта
                    </a>
                </div>

                {featuredProducts.length > 0 && (
                    <div className="hero-proof" aria-label="О коллекции">
                        <span>{formatColorCount(featuredProducts.length)}</span>
                        <span>Актуальное наличие</span>
                        <span>Размер перед заказом</span>
                    </div>
                )}
            </div>
        </section>
    );
}

export default Hero;
